import { useCallback, useEffect, useReducer } from 'react'

import type { InstallState, InstallStatus } from './install-state.js'

import {
  clearDeferredPrompt,
  hasDeferredPrompt,
  promptDeferredInstall,
  subscribeDeferredPrompt,
} from './deferred-install.js'
import { initialInstallState, installReducer } from './install-state.js'

export interface UseInstallPrompt {
  /** Show the deferred native prompt (no-op when none is held). */
  promptInstall: () => Promise<void>
  status: InstallStatus
}

/** Seed the reducer from the module store so a prompt captured before mount isn't missed. */
const initState = (): InstallState => (hasDeferredPrompt() ? { status: 'available' } : initialInstallState)

/**
 * Bridge the browser's install signals onto the pure {@link installReducer}. The deferred
 * `beforeinstallprompt` event is captured at module scope (see {@link subscribeDeferredPrompt})
 * so it survives firing before React mounts; this hook seeds from that store, then reacts to
 * later captures and to `appinstalled`. All lifecycle decisions live in the tested reducer.
 */
export const useInstallPrompt = (): UseInstallPrompt => {
  const [state, dispatch] = useReducer(installReducer, undefined, initState)

  useEffect(() => {
    const unsubscribe = subscribeDeferredPrompt(() => dispatch({ type: 'prompt-captured' }))
    const onAppInstalled = (): void => {
      clearDeferredPrompt()
      dispatch({ type: 'app-installed' })
    }

    window.addEventListener('appinstalled', onAppInstalled)
    return (): void => {
      unsubscribe()
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  const promptInstall = useCallback(async (): Promise<void> => {
    const outcome = await promptDeferredInstall()
    if (outcome !== 'unavailable') dispatch({ outcome, type: 'prompt-result' })
  }, [])

  return { promptInstall, status: state.status }
}
